import React from 'react';
import styled from 'styled-components';
import {DateRangePicker, FlexRow, Icon, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {DateRangePresetDropdown} from './DateRangePresetDropdown';
import {DateScopeDropdown} from './DateScopeDropdown';
import {findRelativeDatePreset} from '../relativeDatePresets';

const tokens = tokensForThemeProvider.sky.sys;

/* the design shows the date-range placeholder uppercase (DD/MM/YYYY). The DS DateRangePicker's
 * react-aria segments render lowercase with no format prop; text-transform inherits into the segment
 * spans. Segment ORDER stays locale-driven — only the case is normalised. */
const PeriodFieldWrapper = styled.div`
	text-transform: uppercase;
`;

/** Read mode's period line, sitting where the editable controls sit in edit mode. Per the design: a
 * calendar signifier and an emphasised label, then the value runs in body weight, with the separators
 * dropped to the auxiliary colour so the dates read as the content and the punctuation recedes. */
const SummaryRow = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
`;

/** Icon plus label. The trailing 16px is the gap to the first value; the label group owns it rather
 * than the row, because the value runs space themselves at 8px. */
const LabelGroup = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
	padding: ${tokens.spacing['200']} ${tokens.spacing['400']} ${tokens.spacing['200']} 0;
`;

const Label = styled.span`
	${tokens.font.label['1']}
	color: ${tokens.color.text.content.main};
	white-space: nowrap;
`;

const Values = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: ${tokens.spacing['200']};
	${tokens.font.body['1']}
	color: ${tokens.color.text.content.main};
`;

/** The two dates of a range sit closer together than the segments do, and read as one value. */
const RangeGroup = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
	white-space: nowrap;
`;

const SummaryValue = styled.span`
	white-space: nowrap;
`;

const Separator = styled.span`
	color: ${tokens.color.text.content.auxiliary};
`;

interface DateRangeChangeEvent {
	startDate: string | null;
	endDate: string | null;
}

/** The reporting period — the range, the relative preset it came from, and the scope it filters on —
 * shared by both top bars so the editable controls and the read-only summary stay in step. Adding a
 * control here surfaces it in read mode in the same change.
 *
 * `readOnly` renders a plain-text summary rather than a disabled control. A greyed-out "Select Date
 * Range" reads as broken rather than informative: it offers an affordance that does nothing and states
 * nothing a reader can use. The summary says what the numbers actually cover instead.
 *
 * It also removes a hazard. The DS DateRangePicker has no working disabled state — it destructures a
 * fixed prop list with no rest spread, so `isDisabled` is dropped outright, and the `disabled` it does
 * accept is forwarded to react-aria's `useDateRangePicker`, which expects `isDisabled`, so it reaches
 * neither the DOM nor the aria state. A reader could tab into the segments and type, dirtying a report
 * they have no permission to save. Not rendering the control at all in read mode is a stronger
 * guarantee than any amount of styling or key-swallowing around a control that will not disable. */
export function ReportPeriodControls({readOnly = false}: {readOnly?: boolean}): JSX.Element {
	const {formatMessage, formatDate} = useIntl();
	const {period, setPeriod} = useReportBuilder();

	const handlePeriodChange = (range: DateRangeChangeEvent): void => {
		setPeriod(range.startDate, range.endDate);
	};

	if (readOnly) {
		const preset = findRelativeDatePreset(period.datePreset);
		const hasRange = period.startDate !== null && period.endDate !== null;

		// Numeric with slashes, matching the design's shape, but the ORDER stays locale-driven: pinning
		// day-first would show a US reader 01/07 for the 7th of January. Same principle the edit-mode
		// range picker follows, whose segments are also ordered by locale.
		//
		// `timeZone: 'UTC'` because the stored value is a plain calendar date with no zone. react-intl
		// hands a string to `new Date()`, which reads it as UTC midnight, so formatting it in the
		// viewer's zone moves it back a day anywhere west of UTC: a report stored 2026-08-01 read
		// 07/31/2026 in California while the edit-mode picker, which parses to a zoneless date, showed
		// the 1st. Formatting in UTC keeps both modes on the date that was actually saved.
		const asDate = (value: string): string =>
			formatDate(value, {day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC'});

		// Only the narrowing scopes are named. `all_dates` is the default every report saved before the
		// scope existed carries, so stating it would add a segment to every summary that tells the reader
		// nothing; the other two change which tasks come back and a reader cannot otherwise tell.
		const scope = period.dateScope;

		const segments: React.ReactNode[] = [];
		if (hasRange) {
			if (preset) {
				segments.push(<SummaryValue key={'preset'}>{formatMessage({id: preset.labelId})}</SummaryValue>);
			}
			segments.push(
				<RangeGroup key={'range'}>
					<span>{asDate(period.startDate as string)}</span>
					<Separator aria-hidden={'true'}>-</Separator>
					<span>{asDate(period.endDate as string)}</span>
				</RangeGroup>
			);
		} else if (period.startDate !== null) {
			// A period can be open at one end -- a null member means that end is unbounded, not that
			// there is no filter. Saying "All Time" for one of those would be flatly untrue in the one
			// mode where the reader has no control to check it against.
			segments.push(
				<SummaryValue key={'from'}>
					{formatMessage({id: 'ai_report_builder.period_from'}, {date: asDate(period.startDate)})}
				</SummaryValue>
			);
		} else if (period.endDate !== null) {
			segments.push(
				<SummaryValue key={'until'}>
					{formatMessage({id: 'ai_report_builder.period_until'}, {date: asDate(period.endDate)})}
				</SummaryValue>
			);
		} else {
			// One value, not two. A report with NEITHER end set applies no date filter at all, and "All
			// Time" is what that means to a reader — the earlier two-part wording explained the mechanism
			// instead.
			segments.push(
				<SummaryValue key={'all-time'}>{formatMessage({id: 'ai_report_builder.period_all_time'})}</SummaryValue>
			);
		}
		if (scope && scope !== 'all_dates') {
			segments.push(
				<SummaryValue key={'scope'}>{formatMessage({id: `ai_report_builder.date_scope.${scope}`})}</SummaryValue>
			);
		}

		return (
			<SummaryRow data-cy={'ai-report-period-summary'}>
				<LabelGroup>
					<Icon icon={'calendar'} size={'m'} newColor={'onlight'} />
					<Label>{formatMessage({id: 'ai_report_builder.period_label'})}</Label>
				</LabelGroup>
				<Values>
					{segments.map((segment, index) => (
						<React.Fragment key={index}>
							{index > 0 ? <Separator aria-hidden={'true'}>&bull;</Separator> : null}
							{segment}
						</React.Fragment>
					))}
				</Values>
			</SummaryRow>
		);
	}

	return (
		<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'} data-cy={'ai-report-period-controls'}>
			<DateRangePresetDropdown />
			<PeriodFieldWrapper data-cy={'ai-report-period-field'}>
				<DateRangePicker
					aria-label={formatMessage({id: 'ai_report_builder.period'})}
					data-cy={'ai-report-period-picker'}
					startDate={period.startDate}
					endDate={period.endDate}
					onChange={handlePeriodChange}
				/>
			</PeriodFieldWrapper>
			<DateScopeDropdown />
		</FlexRow>
	);
}
